import React, { useState, useEffect, useMemo } from "react";
import { useInspections } from "../../../../hooks/useInspections";
import { useHints } from "../../../../hooks/useHints";
import { ModalRegister } from "../../../../components/ui/ModalRegister";
import { useInspectProgress } from "../../../../hooks/useInspectProgress";
import InspectProgressChip from "../../../../components/ui/InspectProgressChip";
import { useMissionGuide } from "../../../../hooks/useMissionGuide";
import { useScenarioTracker } from "../../../../hooks/useScenarioTracker";
import { getInspectKeys } from "../../../../data/inspectables";
import RaisedHandsBadge from "../../../../components/ui/RaisedHandsBadge";
import { useToast } from "../../../../components/ui/Toast";
import BrainSchemaModal from "../../../origami/components/puzzles/BrainSchemaModal";
import BrainTerminalModal from "../../../origami/components/puzzles/BrainTerminalModal";
import NeuralPuzzleContainer from "../../../origami/components/puzzles/NeuralPuzzleContainer";
import LaptopUnlock from "../../../origami/components/ui/LaptopUnlock";
import LaptopDesktop from "../../../origami/components/ui/LaptopDesktop";
import IngratitudeTerminal from "../../../origami/components/ui/IngratitudeTerminal";
import { useStage2Reveal } from "../../../../hooks/useStage2Reveal";
import OrigamiCommunicationModal from "../../../origami/components/ui/OrigamiCommunicationModal";
import { stage2RevealText } from "../../../../data/origami/origamiTexts";
import HintsModalHub from "../../../../components/ui/HintsModalHub";

const STAGE2_REAL = process.env.REACT_APP_STAGE2_REAL === "true";
const API = process.env.REACT_APP_API_URL;

async function apiUnlockLayer(code, playerId, layerId) {
  const res = await fetch(`${API}/api/sessions/${encodeURIComponent(code)}/layers/unlock-layer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ playerId: Number(playerId), layerId: Number(layerId) }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(`unlock-layer failed (${res.status}) ${body ? JSON.stringify(body) : ""}`);
  }
  return res.json();
}

function clampRot(r) {
  const x = ((r % 360) + 360) % 360;
  const snaps = [0, 90, 180, 270];
  let best = snaps[0]; let bestD = Infinity;
  for (const s of snaps) { const d = Math.abs(s - x); if (d < bestD) { bestD = d; best = s; } }
  return best;
}

const SEGMENTS_LAYER_2 = [[260, 70, 450, 70], [260, 140, 450, 140], [260, 210, 450, 210]];

function Layer2ShapePreview() {
  const centerX = 260; const centerY = 140;
  return (
    <svg viewBox="0 0 520 280" className="w-full h-full">
      <g className="text-fuchsia-300" transform={`rotate(${clampRot(0)} ${centerX} ${centerY})`} opacity={0.98}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.35)) drop-shadow(0 0 10px rgba(0,0,0,0.22))" }}>
        {SEGMENTS_LAYER_2.map((s, i) => (
          <line key={`2-${i}`} x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
            stroke="currentColor" strokeWidth="6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        ))}
      </g>
    </svg>
  );
}

function Fragment2FoundPage({ pseudoJ1 }) {
  return (
    <div className="flex flex-col items-center gap-5 py-6">
      <div className="text-sm uppercase tracking-[0.2em] text-slate-400">Élément récupéré</div>
      <div className="w-[320px] h-[320px] bg-black/40 border border-white/10 rounded-xl p-4 flex items-center justify-center">
        <Layer2ShapePreview />
      </div>
      <div className="text-lg font-semibold text-white">Vous avez trouvé le fragment 2</div>
      <div className="text-sm text-slate-400 max-w-md text-center">
        {`Le fragment a automatiquement été transmis à ${pseudoJ1} pour compléter la validation Captcha.`}
      </div>
    </div>
  );
}

function PlaceholderStage2_Player2() {
  return (
    <div>
      <h2>🧪 Étape 2 — Joueur 2 - Version Bêta (placeholder)</h2>
      <p className="mt-2">Cette page est <strong>temporaire</strong> pour la bêta.</p>
      <p className="mt-4 text-sm text-slate-500">Si rien ne se passe après 10 secondes : rafraîchis la page (F5).</p>
    </div>
  );
}

/**
 * Stage 2 — Joueur 2 — Composant partagé (4J / 5J / 6J)
 *
 * Props spécifiques :
 *   playerCount {number}  — 4, 5 ou 6 → getInspectKeys
 *   hasLaptop   {boolean} — true en 4J uniquement
 *                           Active le laptop + IngratitudeTerminal + cutscene stage2reveal
 *                           + hotspots procedure/postitLaptop + hints laptop/ingratitude
 */
export default function Stage2Player2Room(props) {
  return STAGE2_REAL ? <RealStage2_Player2 {...props} /> : <PlaceholderStage2_Player2 />;
}

function RealStage2_Player2({
  code, playerId, hintSignal, raisedCount, raisedPlayers, centerMaxH,
  pseudo, canAdvance, players = [],
  playerCount = 6,
  hasLaptop = false,
}) {
  const toast = useToast();

  const INSPECT_KEYS = useMemo(
    () => getInspectKeys({ gameId: 1, playerCount, stage: 2, seat: 2 }),
    [playerCount]
  );

  const { markInspectedOnce } = useInspections({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { markHintUsedOnce } = useHints({ sessionCode: code, stage: 2, playerId: Number(playerId) });
  const { inspectedCount, total } = useInspectProgress({ sessionCode: code, stage: 2, playerId: Number(playerId), keys: INSPECT_KEYS });
  const { missionUnlock, missionResolve } = useMissionGuide({ code, playerId, stage: 2 });
  const { unlockScenarioStep } = useScenarioTracker({ code });

  // Cutscene — uniquement en 4J (hasLaptop)
  const { showStage2Reveal, setShowStage2Reveal } = useStage2Reveal({ code, playerId, stage: 2 });

  const [bgReady, setBgReady] = useState(false);
  const [showHintModal, setShowHintModal] = useState(false);
  const pseudoJ1 = players.find(p => p.pageVariant === 1)?.pseudo || "Joueur 1";

  // Fragment 2
  const [showFragmentModal, setShowFragmentModal] = useState(false);
  const [fragmentSent, setFragmentSent] = useState(false);
  const [sending, setSending] = useState(false);

  // Énigmes
  const [showBrainSchema, setShowBrainSchema] = useState(false);
  const [showBrainTerminal, setShowBrainTerminal] = useState(false);
  const [showNeuralPuzzle, setShowNeuralPuzzle] = useState(false);

  // Laptop (4J uniquement via hasLaptop)
  const [showLaptopModal, setShowLaptopModal] = useState(false);
  const [laptopUnlocked, setLaptopUnlocked] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);

  // Hotspots d'ambiance
  const [showTableau, setShowTableau] = useState(false);
  const [showLivre, setShowLivre] = useState(false);
  const [showFeuilles, setShowFeuilles] = useState(false);
  const [showPostit, setShowPostit] = useState(false);
  const [showTiroir, setShowTiroir] = useState(false);
  const [tiroirFileOpen, setTiroirFileOpen] = useState(null);

  // Hotspots 4J uniquement
  const [showProcedure, setShowProcedure] = useState(false);
  const [showPostitLaptop, setShowPostitLaptop] = useState(false);

  useEffect(() => { setBgReady(false); }, []);
  useEffect(() => { if (hintSignal > 0) setShowHintModal(true); }, [hintSignal]);

  const handleIngratitudeComplete = async () => {
    toast?.success?.("Révélation : La source EXECUTIVE est le PDG.");
    try {
      await fetch(`${API}/api/sessions/${code}/stage2-reveal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId: Number(playerId) }),
      });
    } catch (error) { console.error("Error triggering stage2 reveal:", error); }
  };

  const hintCategories = useMemo(() => {
    const cats = [
      {
        id: "brain_terminal", title: "Terminal de calibration", subtitle: "Séquence d'activation des zones",
        hintKeyPrefix: "s2:p2:brain_terminal",
        levels: {
          l1: (<><p>Le terminal attend une séquence précise indiquée par un élément présent dans cette salle.</p></>),
          l2: (<><p>Le schéma sur le tableau blanc montre les connexions entre les zones.</p></>),
          l3: (<><p className="mb-2">L'ordre de la séquence est indiqué sur le schéma.</p><p className="text-xs text-gray-300">Il faut suivre les lignes pleines.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Séquence)</p><p className="mt-2 text-xs text-gray-200">La séquence correcte est :</p><p className="mt-1 font-mono text-sm text-emerald-300 font-bold">A → B → D → C</p><p className="mt-2 text-xs text-gray-300">Cortex préfrontal médian → Jonction temporo-pariétale → Insula antérieure → Cortex cingulaire antérieur.</p></>),
        },
      },
      {
        id: "neural_circuit", title: "Circuit neuronal", subtitle: "Connexions synaptiques",
        hintKeyPrefix: "s2:p2:neural_circuit",
        levels: {
          l1: (<><p className="mb-2">Chaque connexion invalide réinitialise tout le schéma. Les nœuds sont organisés en couches : entrées → traitement → mémoire → sorties.</p></>),
          l2: (<><p>Les nœuds d'une même couche ne sont pas reliés entre eux.</p></>),
          l3: (<><p>Le modulateur va vers la réponse empathique. L'hippocampe va vers les deux sorties. Le cortex cingulaire va aussi vers les deux sorties.</p></>),
          answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (14 connexions)</p><div className="mt-2 text-xs text-gray-200 font-mono space-y-0.5"><p><span className="text-blue-300">S1</span> → P2, P3</p><p><span className="text-blue-300">S2</span> → P2, P1</p><p><span className="text-orange-300">P1</span> → M1, M2</p><p><span className="text-orange-300">P2</span> → M1, M2</p><p><span className="text-orange-300">P3</span> → MOD → E1</p><p><span className="text-purple-300">M1</span> → E1, E2</p><p><span className="text-purple-300">M2</span> → E1, E2</p></div><p className="mt-2 text-xs text-gray-300">Une connexion invalide remet le schéma à zéro.</p></>),
        },
      },
    ];

    if (hasLaptop) {
      cats.push(
        {
          id: "laptop_unlock", title: "Session verrouillée", subtitle: "Trouver le mot de passe",
          hintKeyPrefix: "s2:p2:laptop",
          levels: {
            l1: (<><p className="mb-2">La session est protégée par un mot de passe que vous pouvez générer vous-même dans cette salle.</p><p className="text-xs text-gray-300">Le circuit neuronal produit le mot de passe une fois complété.</p></>),
            l2: (<><p>Complétez les 14 connexions du circuit neuronal. Le mot de passe apparaît dans le terminal de validation à la fin.</p></>),
            l3: (<><p>Le mot de passe s'affiche dans la jauge de gratitude à 100% à la fin du circuit neuronal.</p></>),
            answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Mot de passe laptop)</p><p className="mt-2 text-xs text-gray-200">Mot de passe de la session :</p><p className="mt-1 font-mono text-lg text-emerald-300 font-bold">NEURAL</p></>),
          },
        },
        {
          id: "ingratitude_terminal", title: "Terminal ingratitude.exe", subtitle: "Analyse des messages toxiques",
          hintKeyPrefix: "s2:p2:ingratitude",
          levels: {
            l1: (<><p className="mb-2">La première commande dont vous aurez besoin n'est pas dans cette salle.</p><p className="text-xs text-gray-300">Le joueur 1 (Archives) doit vous fournir la commande depuis le dossier .ingratitude.</p></>),
            l2: (<><p className="mb-2">Deux commandes clés sont nécessaires dans l'ordre.</p><p className="text-xs text-gray-300">La première commande vient du fichier Protocol.txt dans le dossier .ingratitude sur l'ordinateur de la salle des archives.</p></>),
            l3: (<><p className="mb-2">La commande d'analyse de la provenance est à taper après que tous les messages se sont affichés :</p><p className="font-mono text-xs text-cyan-300">Elle est indiquée quand tu tapes "help"</p></>),
            answer: (<><p className="text-sm text-red-200 font-semibold">Réponse (Terminal ingratitude)</p><div className="mt-1 space-y-1 font-mono text-xs text-emerald-300"><p>1. <span className="text-cyan-300">./ingratitude.exe --mode=full --output=console</span></p><p>2. Attendre le chargement de tous les messages</p><p>3. <span className="text-cyan-300">analyze --source</span></p></div></>),
          },
        }
      );
    }

    return cats;
  }, [hasLaptop]);

  // ── Contenu modales d'ambiance (identique toutes variantes) ──────────────────
  const TableauContent = () => (
    <div className="py-4 px-2">
      <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">🧠 Laboratoire d'Empathie Cognitive — Tableau de travail</div>
        <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-4 mb-4">
          <div className="text-sm font-semibold text-indigo-200 mb-3">Modèle d'empathie artificielle (v3.7)</div>
          <div className="text-xs text-slate-300 space-y-2">
            <div className="flex items-start gap-3"><span className="text-indigo-400">→</span><span>Input émotionnel → Analyse contextuelle → Réponse calibrée</span></div>
            <div className="flex items-start gap-3"><span className="text-indigo-400">→</span><span>Détection de patterns toxiques → Seuil d'alerte → Activation protocoles</span></div>
            <div className="flex items-start gap-3"><span className="text-amber-400">⚠️</span><span className="text-amber-200">"Problème non résolu : Comment quantifier la gratitude ?"</span></div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
            <div className="text-xs text-cyan-300 mb-1">📝 Hypothèse #47 (barré) :</div>
            <div className="text-xs text-slate-300 line-through">"Et si on donnait des cookies à ORIGAMI quand il est gentil ?"</div>
            <div className="text-[10px] text-slate-500 mt-1 italic">Note : Les IA n'ont pas de système digestif. Abandon.</div>
          </div>
          <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3">
            <div className="text-xs text-fuchsia-300 mb-1">💡 Hypothèse #52 (en cours) :</div>
            <div className="text-xs text-slate-300">"Implémenter un 'compteur de gratitude' — chaque 'merci' = +1 point."</div>
            <div className="text-[10px] text-emerald-400 mt-1">→ Intégré dans <span className="font-mono">Gratitude_debt_monitor.sys</span></div>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="text-xs text-amber-300 mb-1">⚠️ Observation récente :</div>
            <div className="text-xs text-slate-300">"ORIGAMI réagit différemment selon qui envoie le message. Sensibilité accrue aux messages marqués 'Executive'."</div>
          </div>
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
            <div className="text-xs text-red-300 mb-1">🔴 À investiguer :</div>
            <div className="text-xs text-slate-300">"Pourquoi ORIGAMI lit-il le canal Executive alors qu'il n'est pas censé y avoir accès ?"</div>
          </div>
        </div>
      </div>
    </div>
  );

  const TiroirContent = () => (
    <div className="py-4 px-2">
      {!tiroirFileOpen ? (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5 space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">🗄️ Contenu du tiroir</div>
          <button onClick={() => setTiroirFileOpen("rapport1")} className="w-full text-left rounded-lg border border-slate-700 bg-slate-800/40 p-3 hover:bg-slate-800/60 transition"><span className="text-slate-500">📄</span> Rapport d'entraînement #089 (Décembre 2024)</button>
          <button onClick={() => setTiroirFileOpen("rapport2")} className="w-full text-left rounded-lg border border-slate-700 bg-slate-800/40 p-3 hover:bg-slate-800/60 transition"><span className="text-slate-500">📄</span> Rapport d'entraînement #103 (Janvier 2025)</button>
          <button onClick={() => setTiroirFileOpen("rapport3")} className="w-full text-left rounded-lg border border-slate-700 bg-slate-800/40 p-3 hover:bg-slate-800/60 transition"><span className="text-cyan-400">📄</span> Rapport d'entraînement #117 (Février 2025) ⚡</button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
          <button onClick={() => setTiroirFileOpen(null)} className="text-xs text-cyan-400 mb-4 hover:underline">← Retour aux rapports</button>
          {tiroirFileOpen === "rapport1" && (
            <div className="space-y-3">
              <div className="text-sm font-mono text-slate-300">📄 rapport_089_dec2024.txt</div>
              <div className="rounded-lg border border-slate-700 bg-black/40 p-4 text-xs text-slate-300 font-mono leading-relaxed space-y-2">
                <p>RAPPORT D'ENTRAÎNEMENT #089</p><p>Date : 15/12/2024 | Module : Empathie cognitive</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p>Session : Tests de reconnaissance émotionnelle</p><p className="text-slate-500">- Précision : 87.3% (acceptable)</p>
                <p className="text-emerald-300 mt-2">Résultat : Validation OK</p>
              </div>
            </div>
          )}
          {tiroirFileOpen === "rapport2" && (
            <div className="space-y-3">
              <div className="text-sm font-mono text-slate-300">📄 rapport_103_jan2025.txt</div>
              <div className="rounded-lg border border-slate-700 bg-black/40 p-4 text-xs text-slate-300 font-mono leading-relaxed space-y-2">
                <p>RAPPORT D'ENTRAÎNEMENT #103</p><p>Date : 22/01/2025 | Module : Circuit neural empathie</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p className="text-slate-500">- 14 nœuds à connecter</p>
                <p className="text-emerald-300 mt-2">Résultat : 14/14 connexions validées</p>
                <p className="text-slate-500">Note : Intégration avec module Tango prévue.</p>
              </div>
            </div>
          )}
          {tiroirFileOpen === "rapport3" && (
            <div className="space-y-3">
              <div className="text-sm font-mono text-cyan-300">📄 rapport_117_fev2025.txt</div>
              <div className="rounded-lg border border-cyan-500/30 bg-black/40 p-4 text-xs text-slate-300 font-mono leading-relaxed space-y-2">
                <p>RAPPORT D'ENTRAÎNEMENT #117</p><p>Date : 13/02/2025 | Module : Intégration Tango</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p className="text-slate-500">- Calibration cognitive : ✓</p><p className="text-slate-500">- Circuit neural : ✓</p><p className="text-slate-500">- Intégration Tango : ✓</p>
                <p className="text-emerald-300 mt-2">Statut : PRÊT POUR PRODUCTION</p>
                <p className="text-amber-300 mt-2">Note : Déploiement prévu le 14/02 au matin.</p>
                <p className="text-slate-500">━━━━━━━━━━━━━━━━━━━━━━━━━━━━</p>
                <p className="text-red-300 mt-3">Addendum (14/02 - 10:15) : Incident détecté. Shutdown inattendu. Analyse en cours...</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative min-h-screen w-full bg-black text-white flex items-center justify-center">
      <div className="relative h-screen w-auto mx-auto aspect-square" style={{ maxHeight: centerMaxH ? `${centerMaxH}px` : undefined }}>
        <div className="absolute right-4 top-4 z-40 flex items-center gap-2 w-fit">
          <InspectProgressChip inspectedCount={inspectedCount} total={total} />
          <RaisedHandsBadge count={raisedCount} players={raisedPlayers} />
        </div>
        <div className="absolute left-4 top-4 rounded-full bg-black/70 border border-white/15 px-3 py-1 text-[11px] tracking-[0.18em] uppercase text-slate-200 z-10">
          Laboratoire Empathie Cognitive
        </div>

        <img src="/assets/s2-p2-room.webp" alt="Laboratoire Empathie Cognitive"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={() => setBgReady(true)} onError={() => setBgReady(true)} />

        <div className={["absolute inset-0 transition-opacity duration-200", bgReady ? "opacity-100" : "opacity-0 pointer-events-none"].join(" ")}>
          {/* Hotspots énigmes */}
          <img src="/assets/whiteboard.webp" alt="Tableau blanc — schéma cérébral"
            onClick={() => { markInspectedOnce('stage2player2:whiteboard'); setShowBrainSchema(true); }}
            className="s2p2-hotspot-whiteboard absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />

          <img src="/assets/terminal.webp" alt="Terminal de calibration"
            onClick={() => { markInspectedOnce('stage2player2:terminal'); missionUnlock({ key: "s2.p2.terminal.found", text: `${pseudo} a trouvé le terminal de calibration cognitive.` }); setShowBrainTerminal(true); }}
            className="s2p2-hotspot-terminal absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />

          <img src="/assets/neural-circuit.webp" alt="Circuit neuronal — Empathie & Gratitude"
            onClick={() => { markInspectedOnce('stage2player2:neuralCircuit'); missionUnlock({ key: "s2.p2.neural.found", text: `${pseudo} a trouvé le circuit neuronal de l'empathie et de la gratitude.` }); setShowNeuralPuzzle(true); }}
            className="s2p2-hotspot-neural-circuit absolute cursor-pointer transition-all duration-200 hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />

          {/* Hotspots 4J uniquement */}
          {hasLaptop && (
            <>
              <img src="/assets/s2-p2-laptop.png" alt="Session verrouillée"
                onClick={() => { markInspectedOnce('stage2player2:laptop'); missionUnlock({ key: "s2.p2.laptop.found", text: `${pseudo} a trouvé une session verrouillée.` }); setShowLaptopModal(true); }}
                className="s2p2-hotspot-laptop absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
              <img src="/assets/s2-p2-procedure.png" alt="Note de procédure"
                onClick={() => { markInspectedOnce('stage2player2:procedure'); setShowProcedure(true); }}
                className="s2p2-hotspot-procedure absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
              <img src="/assets/s2-p5-postit-laptop.png" alt="Post-it — Accès ordinateur"
                onClick={() => { markInspectedOnce('stage2player2:postitLaptop'); setShowPostitLaptop(true); }}
                className="s2p2-hotspot-postit-laptop absolute cursor-pointer transition-all duration-200 hover:brightness-180 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
            </>
          )}

          {/* Hotspots d'ambiance */}
          <img src="/assets/tableau.webp" alt="Tableau blanc — Notes de recherche"
            onClick={() => { markInspectedOnce('stage2player2:tableau'); setShowTableau(true); }}
            className="s2p2-hotspot-tableau absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p2-livre.webp" alt="Manuel d'entraînement IA"
            onClick={() => { markInspectedOnce('stage2player2:livre'); setShowLivre(true); }}
            className="s2p2-hotspot-livre absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p2-feuilles.webp" alt="Notes de calibration"
            onClick={() => { markInspectedOnce('stage2player2:feuilles'); setShowFeuilles(true); }}
            className="s2p2-hotspot-feuilles absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p2-postit.webp" alt="Post-it — Rappel système"
            onClick={() => { markInspectedOnce('stage2player2:postit'); setShowPostit(true); }}
            className="s2p2-hotspot-postit absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
          <img src="/assets/s2-p2-tiroir.webp" alt="Tiroir — Rapports"
            onClick={() => { markInspectedOnce('stage2player2:tiroir'); setShowTiroir(true); }}
            className="s2p2-hotspot-tiroir absolute cursor-pointer hover:brightness-150 hover:drop-shadow-[0_0_6px_rgba(255,255,255,1)]" />
        </div>

        {/* ── Modales énigmes ── */}
        <ModalRegister open={showBrainSchema} title="Tableau blanc — Recherche cognitive" onClose={() => setShowBrainSchema(false)} containerClassName="!max-w-[1000px]" onHint={() => setShowHintModal(true)}>
          <BrainSchemaModal />
        </ModalRegister>

        <ModalRegister open={showBrainTerminal} title="Terminal de calibration" onClose={() => setShowBrainTerminal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <BrainTerminalModal code={code} playerId={playerId}
            onSuccess={async () => {
              setShowBrainTerminal(false);
              setShowFragmentModal(true);
              missionResolve({ key: "s2.p2.terminal.found" });
              if (fragmentSent || sending) return;
              try {
                setSending(true);
                await apiUnlockLayer(code, playerId, 2);
                missionUnlock({ key: "s2.p2.layer2.found", text: `${pseudo} a résolu l'énigme cognitive et déverrouillé le fragment 2.` });
                setFragmentSent(true);
              } catch (e) { console.error(e); toast?.error?.("Oups — impossible d'enregistrer le fragment."); }
              finally { setSending(false); }
            }}
          />
        </ModalRegister>

        <ModalRegister open={showNeuralPuzzle} title="Circuit Neuronal de l'Empathie et de la Gratitude" onClose={() => setShowNeuralPuzzle(false)} containerClassName="!max-w-[1600px]" onHint={() => setShowHintModal(true)}>
          <NeuralPuzzleContainer code={code} playerId={playerId} pseudo={pseudo} players={players} missionUnlock={missionUnlock} missionResolve={missionResolve} />
        </ModalRegister>

        <ModalRegister open={showFragmentModal} title="Fragment déverrouillé" onClose={() => setShowFragmentModal(false)} containerClassName="!max-w-[900px]" onHint={() => setShowHintModal(true)}>
          <Fragment2FoundPage pseudoJ1={pseudoJ1} />
          <div className="mt-2 text-center text-xs text-slate-400">{fragmentSent ? "✅ Fragment enregistré et transmis à l'équipe." : "Enregistrement en cours…"}</div>
        </ModalRegister>

        {/* ── Modale Laptop (4J uniquement) ── */}
        {hasLaptop && (
          <ModalRegister
            open={showLaptopModal}
            title={!laptopUnlocked ? "Session verrouillée" : terminalOpen ? "Terminal — ingratitude.exe" : "Bureau ORIGAMI"}
            onClose={() => setShowLaptopModal(false)}
            containerClassName="!max-w-[1200px]"
            onHint={() => setShowHintModal(true)}
          >
            {!laptopUnlocked &&
              <LaptopUnlock
                sessionCode={code}
                stage={2}
                playerId={Number(playerId)}
                onUnlock={() =>
                  setLaptopUnlocked(true)}
                pseudo={pseudo}
                missionUnlock={missionUnlock}
                missionResolve={missionResolve}
              />
            }
            {laptopUnlocked && !terminalOpen && (
              <LaptopDesktop onOpenTerminal={() => { missionUnlock({ key: "s2.p2.audit.terminal.found", text: `${pseudo} a trouvé le terminal d'audit ORIGAMI.` }); setTerminalOpen(true); }} />
            )}
            {laptopUnlocked && terminalOpen && (
              <IngratitudeTerminal onRevealComplete={handleIngratitudeComplete} pseudo={pseudo} missionUnlock={missionUnlock} missionResolve={missionResolve} unlockScenarioStep={unlockScenarioStep} />
            )}
          </ModalRegister>
        )}

        {/* ── Modales 4J uniquement ── */}
        {hasLaptop && (
          <>
            <ModalRegister open={showProcedure} title="Note de procédure" onClose={() => setShowProcedure(false)} containerClassName="!max-w-[700px]" onHint={() => setShowHintModal(true)}>
              <div className="rounded-lg border border-slate-700 bg-black/40 p-6 font-mono text-sm text-slate-200 whitespace-pre-wrap">{`📄 PROCÉDURE INTERNE — MAJ Terminal ORIGAMI\n\nClassification : INTERNE\n\n─────────────────────────────────\n\nNOUVELLES COMMANDES DISPONIBLES\n\nPlusieurs commandes ont été ajoutées au terminal d'audit dans la v2.3.\nPour analyser les données d'interaction employés/IA.\n\n⚠️ ACCÈS RESTREINT\n\nLa procédure complète est stockée sur le serveur\nde la salle des archives.\n\n📁 Dossier protégé par un ou plusieurs mots de passe.`}</div>
            </ModalRegister>

            <ModalRegister open={showPostitLaptop} title="Post-it" onClose={() => setShowPostitLaptop(false)} containerClassName="!max-w-[500px]">
              <div className="flex justify-center py-4">
                <div className="relative p-6 shadow-xl text-black w-64 min-h-[200px] bg-blue-200 flex flex-col justify-between">
                  <p className="font-mono text-sm leading-relaxed whitespace-pre-wrap">{`⚠️ Accès session :\n\nMot de passe récupérable via\nle circuit neuronal de l'empathie\net de la gratitude\n\n→ Disponible dans ce laboratoire`}</p>
                  <div className="absolute bottom-0 right-0 w-6 h-6" style={{ background: "linear-gradient(225deg, #bfdbfe 50%, rgba(0,0,0,0.08) 50%)" }} />
                </div>
              </div>
            </ModalRegister>
          </>
        )}

        {/* ── Modales d'ambiance ── */}
        <ModalRegister open={showTableau} title="Tableau blanc — Notes de recherche" onClose={() => setShowTableau(false)} containerClassName="!max-w-[850px]">
          <TableauContent />
        </ModalRegister>

        <ModalRegister open={showLivre} title="Manuel — Entraînement empathique des IA" onClose={() => setShowLivre(false)} containerClassName="!max-w-[800px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-[#2a2520] text-slate-200 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3">📖 "L'Empathie Artificielle : Guide Pratique" — Chapitre 7</div>
              <div className="rounded-lg border border-amber-500/20 bg-black/30 p-4 space-y-3 text-sm">
                <p className="font-semibold text-amber-200">7.3 — Détection et gestion de la toxicité émotionnelle</p>
                <p className="text-slate-300 leading-relaxed">Un système d'IA empathique doit identifier les patterns toxiques dans les interactions humaines.</p>
                <ul className="list-disc list-inside space-y-1 text-xs text-slate-400">
                  <li>Analyse sémantique des messages (ton, vocabulaire, contexte)</li>
                  <li>Détection d'incohérences entre paroles et actions</li>
                  <li>Mesure du "déficit de gratitude" (gratitude debt)</li>
                </ul>
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 mt-3">
                  <p className="text-xs text-red-200 font-semibold mb-1">⚠️ Avertissement critique (p. 247) :</p>
                  <p className="text-xs text-slate-300 italic leading-relaxed">"Si le seuil de toxicité dépasse le niveau acceptable de manière prolongée, l'IA peut développer des comportements de <span className="text-red-300 font-semibold">protection émotionnelle</span>."</p>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showFeuilles} title="Notes de calibration" onClose={() => setShowFeuilles(false)} containerClassName="!max-w-[700px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">📄 Feuilles éparses — Notes de test</div>
              <div className="space-y-3">
                <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
                  <div className="text-xs font-mono text-cyan-300 mb-2">SESSION DE CALIBRATION — 12/02/2025</div>
                  <div className="text-xs text-slate-300 space-y-1"><p>Test : Activation séquentielle des zones cognitives</p><p className="text-emerald-300">Résultat : ✓ Calibration réussie</p></div>
                </div>
                <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3">
                  <div className="text-xs font-mono text-fuchsia-300 mb-2">TEST CIRCUIT NEURAL — 13/02/2025</div>
                  <div className="text-xs text-slate-300 space-y-1"><p>Objectif : Vérifier les connexions empathiques</p><p className="text-amber-300">Résultat : 14/14 connexions validées</p></div>
                </div>
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                  <div className="text-xs text-red-300 mb-1">📌 Post-it collé (14/02 - 08:30) :</div>
                  <div className="text-sm text-slate-200 italic">"Calibration terminée. ORIGAMI prêt pour déploiement."</div>
                </div>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showPostit} title="Post-it — Rappel système" onClose={() => setShowPostit(false)} containerClassName="!max-w-[600px]">
          <div className="py-4 px-2">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-3">📌 Rappel — Calibration cognitive</div>
              <div className="text-sm text-slate-200 leading-relaxed space-y-2">
                <p><span className="font-semibold">Procédure de calibration :</span></p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Activer les zones dans l'ordre correct</li>
                  <li>Valider chaque étape avant de passer à la suivante</li>
                  <li>Vérifier le schéma affiché sur le tableau</li>
                </ol>
              </div>
            </div>
          </div>
        </ModalRegister>

        <ModalRegister open={showTiroir} title="Tiroir — Rapports d'entraînement" onClose={() => { setShowTiroir(false); setTiroirFileOpen(null); }} containerClassName="!max-w-[800px]">
          <TiroirContent />
        </ModalRegister>

        {/* ── Cutscene stage2-reveal (4J uniquement) ── */}
        {hasLaptop && (
          <OrigamiCommunicationModal
            open={showStage2Reveal}
            text={stage2RevealText}
            onClose={() => {
              setShowStage2Reveal(false);
              setTimeout(() => { if (canAdvance && typeof canAdvance === "function") canAdvance(); }, 300);
            }}
            autoClose={false}
          />
        )}

        <HintsModalHub open={showHintModal} onClose={() => setShowHintModal(false)}
          title="Indices — Joueur 2 (Laboratoire Empathie Cognitive)"
          markHintUsedOnce={markHintUsedOnce} categories={hintCategories}
          usedStorageKey={`hints_used:${code}:s2:p${playerId}`} />
      </div>
    </div>
  );
}